import React, { useState } from 'react';
import Icon from '@components/AppIcon';
import Image from '@components/AppImage';
import Button from '@components/ui/Button';

const LocationCard = ({ location, onViewDetails, onVirtualTour }) => {
  const [imageError, setImageError] = useState(false);

  // Default values for missing data
  const specializations = location.specializations || ['General Ophthalmology', 'Vision Care', 'Surgical Services', 'Pediatric Eye Care'];
  const features = location.features || ['Free Parking', 'Wheelchair Access'];
  const hours = location.hours || 'Mon-Fri: 8:30AM-5:00PM, Sat: 8:30AM-1:00PM';

  // Check if this location has virtual tour content
  // Ensure we're checking for tour_views and that it's an array with items
  const hasVirtualTour = location.tour_views && Array.isArray(location.tour_views) && location.tour_views.length > 0;

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden border border-gray-100">
      {/* Location Image */}
      <div className="relative h-48 overflow-hidden">
        {!imageError ? (
          <Image
            src={location.image}
            alt={`${location.name} clinic exterior`}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#08005f] to-[#eb2227] flex items-center justify-center">
            <Icon name="MapPin" size={48} className="text-white opacity-80" />
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col space-y-2">
          {location.isMainBranch && (
            <div className="bg-gradient-to-r from-[#08005f] to-[#eb2227] text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 shadow-md">
              <Icon name="Award" size={16} />
              <span>Main Branch</span>
            </div>
          )}
          {location.isNew && (
            <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              New Location
            </div>
          )}
        </div>
        
        <div className="absolute top-4 right-4 flex flex-col space-y-2">
          {location.emergencyAvailable && (
            <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 shadow-md">
              <Icon name="AlertCircle" size={16} />
              <span>24/7 Emergency</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-5">
        {/* Location Header */}
        <div className="mb-4">
          <h3 className="font-heading font-bold text-xl text-[#08005f] mb-2 line-clamp-1">
            {location.name}
          </h3>
          <div className="flex items-start space-x-2 text-gray-600 mb-3">
            <Icon name="MapPin" size={16} className="mt-1 flex-shrink-0" />
            <p className="text-sm leading-relaxed line-clamp-2">{location.address}</p>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} />
              <span>{hours}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Phone" size={14} />
              <span>{location.phone}</span>
            </div>
          </div>
        </div>

        {/* Specializations */}
        <div className="mb-4">
          <h4 className="font-heading font-semibold text-sm text-[#08005f] mb-2">
            Specializations
          </h4>
          <div className="flex flex-wrap gap-2">
            {specializations.slice(0, 3).map((spec, index) => (
              <span
                key={index}
                className="bg-gradient-to-r from-[#08005f]/10 to-[#eb2227]/10 text-[#08005f] px-2 py-1 rounded-md text-xs font-medium border border-[#08005f]/20"
              >
                {spec}
              </span>
            ))}
            {specializations.length > 3 && (
              <span className="text-[#eb2227] text-xs font-medium">
                +{specializations.length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-3">
            {features.slice(0, 4).map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Check" size={14} className="text-green-600 flex-shrink-0" />
                <span className="text-xs text-gray-600">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <div className={`grid ${hasVirtualTour ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
            {hasVirtualTour && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onVirtualTour(location)}
                iconName="Eye"
                iconPosition="left"
                iconSize={14}
                className="border-[#08005f] text-[#08005f] hover:bg-[#08005f] hover:text-white"
              >
                Virtual Tour
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(location)}
              iconName="Info"
              iconPosition="left"
              iconSize={14}
              className="border-[#08005f] text-[#08005f] hover:bg-[#08005f] hover:text-white"
            >
              Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationCard;